import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import useKeyPress from '@scentregroup/shared/hooks/use-key-press';
import { useAnalytics } from '../../analytics';
import ClientOnly from '../client-only';
import { useUniversalHeaderCart } from '../../hooks/use-cart';
import SearchResults from './search-results';
import SearchContainer from './search-container';
import { isEmbeddedSearchProps } from './types';
import useAlgolia from './use-algolia';
import groupAndRenderSegments from './group-and-render-segments';
import { makeOnResultClick, logHideSearch, logShowSearch, logOnResultsChange } from './analytics';
import SearchModalAutocomplete from './search-modal-autocomplete';
import usePlaceholder from './use-placeholder';
import { useUniversalHeaderSearch } from './use-universal-header-search';
import { useLocalUser } from '../../hooks/use-local-user';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ESCAPE_CODE = 'Escape';

function useShowHide() {
  const {
    showSearch,
    setShowSearch
  } = useUniversalHeaderSearch();
  const hide = useCallback(() => {
    window.scroll(0, 0);
    setShowSearch(false);
  }, [setShowSearch]);
  const show = useCallback(() => {
    setShowSearch(true);
    logShowSearch();
  }, [setShowSearch]);
  return [showSearch, show, hide];
}

function useHistoryListener(listener) {
  const history = useHistory();
  useEffect(() => history.listen ? history.listen(listener) : () => null, [listener, history]);
}

function processResults(query, context, segmentHits) {
  const onResultClickHeading = makeOnResultClick(query);
  const results = groupAndRenderSegments({
    segmentResults: segmentHits,
    context,
    onResultClickHeading
  });
  const showNoResults = query.length > 0 && results.length === 0;
  return {
    showNoResults,
    results
  };
}

const Search = props => {
  const {
    specialSearchKind
  } = useUniversalHeaderSearch();
  const {
    algoliaApiKeys,
    country,
    centre,
    directCategories
  } = props;
  const initialQuery = isEmbeddedSearchProps(props) ? props.showFixedQuery : undefined;
  const {
    query,
    results: segmentHits,
    isInitialised
  } = useAlgolia(algoliaApiKeys, country, centre, initialQuery);
  const [isVisible, show, hide] = useShowHide();

  const hideManual = () => {
    logHideSearch(query);
    hide();
  };

  useKeyPress({
    [ESCAPE_CODE]: hideManual
  });
  useHistoryListener(hide); // If the URL changes hide the search modal

  const analytics = useAnalytics();
  const user = useLocalUser();

  const onSubmit = async (_event, newQuery) => {
    if (newQuery.length > 0 && true) {
      analytics.trackEvent({
        object: 'Global',
        action: 'Searched',
        properties: _objectSpread(_objectSpread({
          category: 'Search',
          label: 'global_searched',
          search_type: centre ? 'Centre' : 'Westfield Direct'
        }, centre && {
          centre_context: centre.title
        }), {}, {
          keyword: newQuery
        })
      });
      const centrePath = centre ? "/".concat(centre.slug) : '';
      const q = encodeURIComponent(newQuery); // TODO when all clients are under one routing
      // use history.push from the router

      window.location.assign("".concat(centrePath, "/search?q=").concat(q)); // // Required when using history.push:
      // hide() // in the event we're already on /search
      // setIsUiLoading(false)
    }
  };

  const {
    count
  } = useUniversalHeaderCart();
  const context = {
    centre,
    country,
    specialSearchKind,
    mkpCartCount: count,
    query,
    user
  };
  const {
    results
  } = processResults(query, context, segmentHits);
  useEffect(() => {
    const numHits = results.reduce((acc, _ref) => {
      let {
        hits
      } = _ref;
      return acc + hits.length;
    }, 0);

    if (query && query.length > 0) {
      logOnResultsChange(query, numHits);
    }
  }, [query, results]);
  const displayMode = isVisible ? 'visibleModal' : 'hiddenModal';
  const placeholder = usePlaceholder(context);

  if (!isInitialised) {
    return null;
  }

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SearchContainer, {
      displayMode: displayMode,
      onClickClose: hideManual,
      "data-id": "search_index_Search_SearchContainer",
      children: /*#__PURE__*/_jsx(SearchModalAutocomplete, {
        placeholder: placeholder,
        algoliaApiKeys: algoliaApiKeys,
        baseContext: context,
        directCategories: directCategories,
        onClickClose: hideManual,
        onSubmit: onSubmit,
        "data-id": "search_index_Search_SearchContainer_SearchModalAutocomplete"
      })
    }), props.children(show)]
  });
};

export const EmbeddedSearch = props => {
  const {
    country,
    centre,
    showFixedQuery,
    algoliaResults,
    mkpCartCount
  } = props;
  const user = useLocalUser();
  const baseContext = {
    user,
    centre,
    country,
    mkpCartCount,
    query: showFixedQuery
  };
  const {
    results,
    showNoResults
  } = processResults(showFixedQuery, baseContext, algoliaResults || {});
  return /*#__PURE__*/_jsx(SearchContainer, {
    displayMode: "embedded",
    "data-id": "search_index_Search_SearchContainer",
    children: /*#__PURE__*/_jsx(ClientOnly, {
      "data-id": "search_index_Search_SearchContainer_ClientOnly",
      children: /*#__PURE__*/_jsx(SearchResults, {
        context: baseContext,
        isLoading: false,
        showNoResults: showNoResults,
        results: results,
        showFixedQuery: showFixedQuery,
        "data-id": "search_index_Search_ClientOnly_SearchResults"
      })
    })
  });
};

const ModalSearch = props => /*#__PURE__*/_jsx(Search, _objectSpread(_objectSpread({}, props), {}, {
  "data-id": "search_index_Search"
}));

export default ModalSearch;